import { action, observable, makeAutoObservable } from 'mobx';
import { CategoryType } from 'src/domains/casino/models/models';
import { CategoriesAndGamesCollection } from 'src/domains/casino/state/AppCasino/store/game-store/Game.store';

export class CategoryState {
    @observable public categoryRef: HTMLDivElement | null = null;
    @observable public showGames: boolean = true;

    public constructor(
        private readonly categoryId: number,
        private readonly collectionData: CategoriesAndGamesCollection
    ) {
        makeAutoObservable(this);
    }

    public setCategoryRef = (ref: HTMLDivElement | null): void => {
        this.categoryRef = ref;
    };

    @action public toggleShowGames = (): void => {
        this.showGames = !this.showGames;
    };

    private get data(): CategoryType | null {
        return this.collectionData.categories.find((category) => category.id === this.categoryId) ?? null;
    }

    public get id(): number {
        return this.categoryId;
    }

    public get name(): string {
        return this.data?.name ?? '';
    }

    public get active(): boolean {
        return this.data?.active ?? false;
    }

    public get fixed(): boolean {
        return this.data?.fixed ?? false;
    }

    public get visibleInNavigation(): boolean {
        return this.data?.visible_in_navigation ?? false;
    }

    public get visibleInAllGames(): boolean {
        return this.data?.visible_in_all_games ?? false;
    }

    private get currentCategoryPosition(): number {
        return this.collectionData.getCategoryPosition(this.id);
    }

    private get minAllowedPosition(): number {
        return this.collectionData.casinoStoreEnabled ? 1 : 0;
    }

    public get isUpMovePossible(): boolean {
        if (this.fixed) {
            return false;
        }

        if (this.currentCategoryPosition === this.minAllowedPosition) {
            return false;
        }

        const newPosition = this.currentCategoryPosition - 1;

        if (newPosition < this.collectionData.getFixedCount() + this.minAllowedPosition) {
            return false;
        }
        return true;
    }

    public get isDownMovePossible(): boolean {
        if (this.fixed) {
            return false;
        }

        const newPosition = this.currentCategoryPosition + 1;

        if (newPosition >= this.collectionData.categories.length + this.minAllowedPosition) {
            return false;
        }
        return true;
    }

    public get gameIds(): Array<number> {
        // In old cms it was bug: non existing id of the games can be returned in category.game_ids.
        // We need aditional check if game exists.
        // This checking should be removed after deploy casino-store and replaced by:
        // return this.data?.game_ids ?? [];
        if (this.data?.game_ids === undefined) {
            return [];
        }

        const gameIds: Array<number> = [];

        for (const gameId of this.data.game_ids) {
            if (this.isAssignedGameExists(gameId)) {
                gameIds.push(gameId);
            }
        }
        return gameIds;
    }

    private isAssignedGameExists = (gameId: number): boolean => {
        if (this.collectionData.games.filter((game) => game.id === gameId).length > 0) {
            return true;
        }
        return false;
    };
}
